import React from 'react';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import TaskItem from '@tiptap/extension-task-item';

import { NodeViewProps } from '@tiptap/core';
import { TaskItemOptions } from '@tiptap/extension-task-item/src/task-item';

import {
    createNewEmptyTask,
    handleEmptyFirstTaskDeletion,
    handleEmptyFirstTaskToTitle,
    handleFirstTitledTaskBackspace,
    handleModifiedDeleteShortcut,
    handleTaskItemWithNestedPreviousItem,
} from './taskListItemKeyboardHandlers';
import { createTaskToTitleInputRule } from './taskListTitleInputRules';
import TaskListItemDeleteButton from './TaskListItemDeleteButton';
import TaskListItemDragHandle from './TaskListItemDragHandle';
import { deleteTaskItem, TaskDeletionInfo } from './taskListItemDeletionUtils';
import { Checkbox } from '@milanote/roma';
import type { TaskItemChipAttrs } from './taskListItemChipTypes';

/**
 * Props for the injected chips renderer. Typed only against `src/common` attr
 * types so the concrete `src/client` chips component can be injected via options
 * without `src/common` importing `src/client` (mirrors the checkbox/delete pattern).
 */
export type TaskItemChipsComponentProps = {
    attrs: TaskItemChipAttrs & { checked?: boolean };
    updateAttributes: (attrs: Partial<TaskItemChipAttrs>) => void;
};

type TaskListItemOptions = TaskItemOptions & {
    DeleteIconComponent: React.ComponentType;
    DragHandleComponent: React.ComponentType;
    ChipsComponent: React.ComponentType<TaskItemChipsComponentProps>;
    onDeleteTask?: (info: TaskDeletionInfo) => void;
};

/**
 * Renders a task list item: the Roma checkbox plus the injected (client-supplied)
 * delete icon, drag handle, and chips components.
 */
const TaskListItemDisplay: React.FC<NodeViewProps> = (props) => {
    const { node, editor, getPos, updateAttributes, extension } = props;
    const { DeleteIconComponent, DragHandleComponent, ChipsComponent, onDeleteTask } = extension.options;

    const onClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        updateAttributes({ checked: !node.attrs.checked });
    };

    const onDelete = () => deleteTaskItem(editor, getPos(), onDeleteTask);

    return (
        <NodeViewWrapper className="task-item-row">
            {/*
                The Roma checkbox is presentational, so the click-to-toggle lives on this
                non-editable wrapper (ProseMirror treats it as node chrome). `display: contents`
                keeps the checkbox's own `.RomaCheckboxBase` element as the row's flex child.
            */}
            <div contentEditable={false} onClick={onClick} style={{ display: 'contents' }}>
                <Checkbox.Base isSelected={node.attrs.checked} variant="tertiary" />
            </div>
            {/*
                Rendered before `.task-item-main` (which holds nested sub-task rows and their own end-actions)
                so that, with equal z-index, this row's actions paint *below* any nested row's actions.
                That lets the deepest hovered row's gutter win the hover hit-test (see TaskListEditor.scss).
                Non-editable so ProseMirror treats it as node chrome, not content.
            */}
            <div className="task-item-end-actions" contentEditable={false}>
                <TaskListItemDeleteButton
                    show={node.attrs.checked}
                    DeleteIcon={DeleteIconComponent}
                    onDelete={onDelete}
                />
                <TaskListItemDragHandle DragHandleComponent={DragHandleComponent} />
            </div>
            <div className="task-item-main">
                <NodeViewContent className="task-item-content" />
                <div
                    className="task-item-chips"
                    contentEditable={false}
                    // Outside NodeViewContent and non-editable so ProseMirror treats the
                    // chips as node chrome; stop pointer propagation so clicking a chip
                    // does not move the editor selection (mirrors the checkbox pattern).
                    onMouseDown={(event) => event.stopPropagation()}
                >
                    <ChipsComponent
                        attrs={node.attrs as TaskItemChipsComponentProps['attrs']}
                        updateAttributes={updateAttributes}
                    />
                </div>
            </div>
        </NodeViewWrapper>
    );
};

export const TaskListItem = TaskItem.extend<TaskListItemOptions>({
    addAttributes() {
        return {
            // Preserve the base task item attrs (e.g. `checked`).
            ...this.parent?.(),
            // Per-task chip data. `keepOnSplit: false` so pressing Enter yields a blank
            // task rather than inheriting the previous task's due date/labels/reactions.
            // These attrs are in-document only (`rendered: false`); this POC does not
            // persist or HTML-serialise chip data.
            // The stable `taskId` is supplied separately by the `UniqueID` extension
            // (configured where the editor composes its extensions), mirroring the card editor.
            dueDate: { default: null, keepOnSplit: false, rendered: false },
            assignments: { default: [], keepOnSplit: false, rendered: false },
            labels: { default: [], keepOnSplit: false, rendered: false },
            reactions: { default: [], keepOnSplit: false, rendered: false },
            // Gates the empty "Assign to" placeholder chip (mirrors the element
            // `showAssignments` flag): set when the Assign tool is used so the
            // placeholder persists even before an assignee is chosen, and stays
            // shown if the user dismisses the popup without picking anyone.
            showAssignments: { default: false, keepOnSplit: false, rendered: false },
        };
    },

    addInputRules() {
        // Convert an empty first task into a title when "# " is typed (preserving any base rules).
        return [...(this.parent?.() ?? []), createTaskToTitleInputRule()];
    },

    addNodeView() {
        return ReactNodeViewRenderer(TaskListItemDisplay, {
            as: 'li',
            attrs: ({ node }) => ({
                'data-checked': String(node.attrs.checked),
            }),
        });
    },

    addKeyboardShortcuts() {
        const triggerModifiedDeleteShortcut = () =>
            this.editor.commands.first((props) => [() => handleModifiedDeleteShortcut(props, this.name)]);

        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            Backspace: () =>
                this.editor.commands.first((props) => [
                    () => handleEmptyFirstTaskToTitle(props, this.name),
                    () => handleFirstTitledTaskBackspace(props, this.name),
                    () => handleEmptyFirstTaskDeletion(props, this.name),
                    () => handleTaskItemWithNestedPreviousItem(props, this.name),
                ]),
            Enter: () =>
                this.editor.commands.first((props) => [
                    () => props.commands.splitListItem(this.name),
                    () => createNewEmptyTask(props, this.name),
                ]),
            'Mod-Delete': triggerModifiedDeleteShortcut,
            'Alt-Delete': triggerModifiedDeleteShortcut,
            'Mod-Backspace': triggerModifiedDeleteShortcut,
            'Alt-Backspace': triggerModifiedDeleteShortcut,
            'Shift-Tab': () => {
                this.editor.commands.liftListItem(this.name);
                // Intentionally always return true to prevent focus from leaving the editor
                return true;
            },
            Tab: () => {
                this.editor.commands.sinkListItem(this.name);
                // Intentionally always return true to prevent focus from leaving the editor
                return true;
            },
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    },
});
